import { useMemo } from "react";

import { compareAsc } from "date-fns";
import { first, sortBy } from "lodash";

import {
  AnswerFragment,
  ReportFragment,
} from "../../../graphql/generated/definitions";
import { QuestionWithAnswers } from "../types/ReportFormTypes";
import useStore from "../../../store";

/**
 * Return previous answers for the question.
 * If no answer are provided, add empty answers when needed depending on the state of the report
 * Answers are sorted by role:
 * - Collaborator
 * - Referent by date ascending
 */
export default function usePreviousAnswers(
  question: QuestionWithAnswers,
  report: ReportFragment,
  shouldOmitDraft = true
) {
  const [reportRole] = useStore((state) => state.report.viewedReportState)

  return useMemo(() => {
    const submittedAnswers: Partial<AnswerFragment>[] = question.answers
      .filter(({ isDraft }) => (shouldOmitDraft ? !isDraft : true))
      .sort((answer1, answer2) => {
        if (answer1.isCollaborator) return -1;
        if (answer2.isCollaborator) return 1;

        return compareAsc(
          new Date(answer1.createdAt),
          new Date(answer2.createdAt)
        );
      });

    // The first answer is not from the collaborator but the collaborator has already submitted his report
    if (
      report.filledAt !== null &&
      question.visibility !== "referents" &&
      !first(submittedAnswers)?.isCollaborator
    )
      submittedAnswers.unshift({
        user: report.review.collaborator,
        isCollaborator: true,
        isEmpty: true,
      });

    sortBy(report.reportReferents, "referent.name").forEach(
      ({ filledAt, referent }) => {
        // The referent has already submitted but haven't answered
        if (
          filledAt !== null &&
          question.visibility !== "collaborator" &&
          reportRole !== "collaborator" &&
          !submittedAnswers.find(({ userId }) => userId === referent.id)
        )
          submittedAnswers.push({
            user: referent,
            isCollaborator: false,
            isEmpty: true,
          });
      }
    );

    return submittedAnswers;
  }, [report, question, shouldOmitDraft]);
}
